import { CAMPAIGN_THEMES, type Campaign, type CampaignDraft, type CampaignStep, type CampaignTheme } from '../api/crm';

export const MAX_CAMPAIGN_STEPS = 8;
export const campaignThemeLabels: Record<CampaignTheme, string> = {
  PREVIEW: 'Branded Wi-Fi preview', ADS: 'Ads and promotions', SURVEYS: 'Surveys',
  ANALYTICS: 'Location and visit analytics', CUSTOM: 'Custom email'
};

export interface CampaignEditor {
  campaignId: number | null;
  revision: number | null;
  baseline: CampaignDraft;
  draft: CampaignDraft;
  selectedStepId: string | null;
}

export interface CampaignSaveReceipt {
  campaignId: number | null;
  revision: number | null;
  draft: CampaignDraft;
}

export function copyCampaignDraft(campaign: CampaignDraft): CampaignDraft {
  return { name: campaign.name, status: campaign.status, steps: campaign.steps.map(step => ({ ...step })) };
}

function campaignIdentity(campaign: Campaign | CampaignDraft): number | null {
  return 'id' in campaign ? campaign.id : null;
}

function sameDraft(left: CampaignDraft, right: CampaignDraft): boolean {
  return left.name === right.name && left.status === right.status && left.steps.length === right.steps.length &&
    left.steps.every((step, index) => {
      const other = right.steps[index];
      return step.id === other.id && step.title === other.title && step.theme === other.theme &&
        step.delay_days === other.delay_days && step.subject_guidance === other.subject_guidance &&
        step.body_guidance === other.body_guidance && step.preview_required === other.preview_required;
    });
}

export function createCampaignEditor(campaign: Campaign | CampaignDraft, selectedStepId?: string | null): CampaignEditor {
  return {
    campaignId: campaignIdentity(campaign), revision: 'revision' in campaign ? campaign.revision : null,
    baseline: copyCampaignDraft(campaign), draft: copyCampaignDraft(campaign),
    selectedStepId: campaign.steps.some(step => step.id === selectedStepId) ? selectedStepId! : null
  };
}

export function campaignEditorIsDirty(editor: CampaignEditor | null): boolean {
  return !!editor && !sameDraft(editor.baseline, editor.draft);
}

/** A successful save adopts the authoritative revision; background reads preserve dirty edits. */
export function reconcileCampaignEditor(editor: CampaignEditor | null, campaign: Campaign | CampaignDraft, receipt?: CampaignSaveReceipt | null): CampaignEditor {
  if (!editor || editor.campaignId !== campaignIdentity(campaign)) return createCampaignEditor(campaign, editor?.selectedStepId);
  const accepted = receipt && receipt.campaignId === editor.campaignId && sameDraft(editor.draft, receipt.draft) &&
    'revision' in campaign && campaign.revision > (receipt.revision ?? 0);
  return accepted || !campaignEditorIsDirty(editor) ? createCampaignEditor(campaign, editor.selectedStepId) : editor;
}

export function campaignDays(steps: CampaignStep[]): number[] {
  let day = 0;
  return steps.map(step => { day += Number.isFinite(step.delay_days) ? step.delay_days : 0; return day; });
}

/** Waits describe positions in the sequence, so moving content preserves the cadence. */
export function moveCampaignStep(editor: CampaignEditor, id: string, direction: -1 | 1): CampaignEditor {
  const index = editor.draft.steps.findIndex(step => step.id === id), next = index + direction;
  if (index < 0 || next < 0 || next >= editor.draft.steps.length) return editor;
  const waits = editor.draft.steps.map(step => step.delay_days), steps = [...editor.draft.steps];
  [steps[index], steps[next]] = [steps[next], steps[index]];
  return { ...editor, draft: { ...editor.draft, steps: steps.map((step, position) => ({ ...step, delay_days: waits[position] })) } };
}

export function addCampaignStep(editor: CampaignEditor, id: string): CampaignEditor {
  if (editor.draft.steps.length >= MAX_CAMPAIGN_STEPS || editor.draft.steps.some(step => step.id === id)) return editor;
  const step: CampaignStep = { id, title: 'New email', theme: 'CUSTOM', delay_days: editor.draft.steps.length ? 7 : 0,
    subject_guidance: '', body_guidance: '', preview_required: false };
  return { ...editor, selectedStepId: id, draft: { ...editor.draft, steps: [...editor.draft.steps, step] } };
}

export function removeCampaignStep(editor: CampaignEditor, id: string): CampaignEditor {
  if (editor.draft.steps.length <= 1) return editor;
  const position = editor.draft.steps.findIndex(step => step.id === id);
  if (position < 0) return editor;
  const steps = editor.draft.steps.filter(step => step.id !== id).map((step, index) => index ? step : { ...step, delay_days: 0 });
  return { ...editor, draft: { ...editor.draft, steps },
    selectedStepId: editor.selectedStepId === id ? steps[Math.min(position, steps.length - 1)].id : editor.selectedStepId };
}

export function campaignDraftErrors(draft: CampaignDraft): string[] {
  const errors: string[] = [];
  if (!draft.name.trim() || draft.name.length > 120) errors.push('Give the campaign a name of up to 120 characters.');
  if (!draft.steps.length || draft.steps.length > MAX_CAMPAIGN_STEPS) errors.push('Use between 1 and 8 email steps.');
  if (new Set(draft.steps.map(step => step.id)).size !== draft.steps.length) errors.push('Each step needs a unique identity. Reload the saved draft.');
  draft.steps.forEach((step, index) => {
    const prefix = 'Step ' + (index + 1) + ': ';
    if (!/^[a-zA-Z][a-zA-Z0-9_-]{0,63}$/.test(step.id)) errors.push(prefix + 'the step identity is invalid. Reload the saved draft.');
    if (!step.title.trim() || step.title.length > 100) errors.push(prefix + 'add a title of up to 100 characters.');
    if (!(CAMPAIGN_THEMES as readonly string[]).includes(step.theme)) errors.push(prefix + 'choose a supported topic.');
    if (!Number.isInteger(step.delay_days) || (index === 0 ? step.delay_days !== 0 : step.delay_days < 1 || step.delay_days > 30)) errors.push(prefix + (index === 0 ? 'the first email starts on day 0.' : 'choose a wait of 1 to 30 whole days.'));
    if (step.subject_guidance.length > 240) errors.push(prefix + 'keep subject guidance within 240 characters.');
    if (step.body_guidance.length > 2000) errors.push(prefix + 'keep message guidance within 2,000 characters.');
  });
  return errors;
}
